import React, { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PipelineDetail, PipelineTable } from "@/components/agency/Pipeline";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function AgencyLeads() {
  const [d, setD] = useState(null);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [add, setAdd] = useState(false);
  const { viewAs } = useAuth();
  const load = useCallback(() => api.get("/agency/pipeline", { params: { kind: "lead", status, q } }).then((r) => setD(r.data)).catch((e) => toast.error(apiError(e))), [status, q]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  const total = d ? Object.values(d.counts).reduce((a, b) => a + b, 0) : 0;
  return (
    <div className="max-w-[1200px] mx-auto" data-testid="agency-leads-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div><h1 className="font-display text-3xl font-semibold text-slate-900">Leads</h1>
          <p className="text-slate-500 mt-1">Insurance sales leads shared by your Agency team.</p></div>
        {!viewAs && <Button onClick={() => setAdd(true)} className="bg-brand hover:bg-brand-strong" data-testid="add-lead-btn"><Plus className="w-4 h-4 mr-1" />Add Lead</Button>}
      </div>
      <div className="flex flex-wrap items-center gap-1.5 mb-4" data-testid="lead-status-filters">
        {[["", `All (${total})`], ...(d?.statuses || []).map((s) => [s, `${s} (${d.counts[s] || 0})`])].map(([k, l]) => (
          <button key={k || "all"} onClick={() => setStatus(k)} data-testid={`lead-filter-${k || "all"}`}
            className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${status === k ? "bg-slate-800 text-white border-slate-800" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{l}</button>))}
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search leads…" className="h-8 max-w-xs ml-auto" data-testid="lead-search" />
      </div>
      {d && <PipelineTable rows={d.items} onOpen={setOpen} kind="lead" />}
      {open && <PipelineDetail id={open} onClose={() => setOpen(null)} onChanged={load} />}
      {add && <AddLead onClose={() => setAdd(false)} onSaved={(id) => { setAdd(false); load(); setOpen(id); }} />}
    </div>
  );
}

function AddLead({ onClose, onSaved }) {
  const [f, setF] = useState({ first_name: "", last_name: "", phone: "", email: "", source: "", next_follow_up: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => { setSaving(true); try { const { data } = await api.post("/agency/leads", f); toast.success("Lead added"); onSaved(data.id); } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg" data-testid="add-lead-dialog">
        <DialogHeader><DialogTitle>Add Lead</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Input placeholder="First name *" value={f.first_name} onChange={set("first_name")} data-testid="lead-first" />
          <Input placeholder="Last name" value={f.last_name} onChange={set("last_name")} data-testid="lead-last" />
          <Input placeholder="Phone" value={f.phone} onChange={set("phone")} data-testid="lead-phone" />
          <Input placeholder="Email" value={f.email} onChange={set("email")} data-testid="lead-email" />
          <Input placeholder="Source (e.g. Referral, Walk-in)" value={f.source} onChange={set("source")} data-testid="lead-source" />
          <Input type="date" value={f.next_follow_up} onChange={set("next_follow_up")} data-testid="lead-followup" />
          <Textarea className="col-span-2" rows={3} placeholder="Notes" value={f.notes} onChange={set("notes")} data-testid="lead-notes" />
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || !f.first_name.trim()} className="bg-brand hover:bg-brand-strong" data-testid="lead-save">{saving ? "Saving…" : "Add Lead"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
